import { cn } from '@invent/wl-ui-kit-next';
import { Button, Checkbox } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { DataTable, StatusChip, type DataTableState, type IDataColumn } from '@/design';
import { ApiError } from '@/domains/api';
import type {
  IListField,
  IListImport,
  IListImportChange,
  IListImportRow,
  IListImportRowList,
  ListImportRowFilter
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, Pager, PastTheEnd, useAdminData } from '../../components/admin-page';
import { CardLink, personHubPath } from '../../components/participant-link';
import { formatMoney } from '../../components/sec-adv-values';
import { changeText } from '../../figures';
import { formatListValue, holdLabel, importOutcomeChip, optionLabel } from '../../lists';
import { refusalMessage } from '../../refusal';
import { LONG_TEXT_WHOLE } from '../lists/cells';

/**
 * An import's rows (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §5.3) — what the check found, row by row: `GET /imports/{id}/rows`, survey-api's page of 100 under
 * one of five tabs (`ListImportRowFilters`: All, the rows that would change, the held, the errors,
 * the unmatched), in its order (by row number). Nothing here counts, sorts or judges a row.
 *
 * **A row in words**: its number in the file, the person (`participantName`, linked to the record by
 * `participantId`) with the key as written beside it — "Nobody matches" where none did — the
 * outcome, and each change as *field: before → after* in the field's own words and formats
 * (`changeValueText`). A held one carries its reason, and an anomaly what it was measured against —
 * "+240.00% against $1.2 B" — the one arithmetic here, a display of survey-api's `baseline`; whether
 * the change is unusual is survey-api's `anomaly`. A first value is marked "never held". After the
 * apply and the reversal, what each did is said per change (`applied`, `reverted`, a `moved` or
 * `edited` hold).
 *
 * **Write this one too** ticks a held row into the apply's `includeHeld` (row numbers, the wire's
 * unit) while the import is checked and not applied; **Include every held change** ticks the held
 * rows of the page on screen, and says so — the rows of other pages stay as they are.
 */

/** survey-api's page of an import's rows (`IListImports.RowPageSize`). */
export const IMPORT_ROW_PAGE_SIZE = 100;

export const ROW_TABS: readonly { filter: Nullable<ListImportRowFilter>; label: string }[] = [
  { filter: null, label: 'All' },
  { filter: 'changed', label: 'Would change' },
  { filter: 'held', label: 'Held' },
  { filter: 'error', label: 'Not written' },
  { filter: 'unmatched', label: 'Match nobody' }
];

/** The catalogue's field by code — the import's survey's, as the reader may see it. */
export type FieldOf = (code: string) => IListField | undefined;

const FIGURE_DATED = /^fig\.([^.]+)\.([^.]+)\.(\d{4}-\d{2}-\d{2})$/;
const PLACEMENT = /^rank\.([^.]+)\.(\d+)(\.territory)?$/;

/**
 * A field an import writes, in words: the catalogue's label for its code — an internal field, a
 * figure at an as-of date, a placement's list and year, as survey-api's catalogue names them. A
 * code the catalogue holds no value of yet is read from its pattern (`ListImportColumns.Pattern`):
 * a dated figure by its series' label, a placement by its list's label (the *Named in* options),
 * its territory beside it; anything else as spelled.
 */
export function importFieldLabel(code: string, fieldOf: FieldOf): string {
  const known = fieldOf(code);
  if (known) return known.label;
  const figure = FIGURE_DATED.exec(code);
  if (figure) {
    const series = fieldOf(`fig.${figure[1]}.${figure[2]}`);
    return series ? `${series.label}, as of ${figure[3]}` : code;
  }
  const placement = PLACEMENT.exec(code);
  if (placement) {
    const place = fieldOf(`rank.${placement[1]}.${placement[2]}`)?.label;
    const named = fieldOf('rank.named_in');
    const list = place ?? (named ? `${optionLabel(named, placement[1])} ${placement[2]}` : null);
    if (list === null) return code;
    return placement[3] ? `${list} — territory` : list;
  }
  return code;
}

/** A change's `before` or `after` in the field's words. The stored forms are survey-api's: an
 *  internal field's `{t, v}`, a figure's number, a placement's `{place, territory}`; null an empty
 *  cell. A value of another shape is drawn as written. */
export function changeValueText(code: string, value: unknown, fieldOf: FieldOf): string {
  if (value === null || value === undefined) return '(blank)';
  if (code.startsWith('fig.')) return typeof value === 'number' ? formatMoney(value) : written(value);
  if (code.startsWith('rank.') && isRecord(value) && 'place' in value) {
    const place = value.place === null || value.place === undefined ? '(no place)' : written(value.place);
    return typeof value.territory === 'string' && value.territory !== ''
      ? `${place} · ${value.territory}`
      : place;
  }
  const inner = isRecord(value) && 't' in value && 'v' in value ? value.v : value;
  const field = fieldOf(code);
  if (!field) return written(inner);
  return formatListValue(field, inner) || '(blank)';
}

/** "+240.00% against $1.2 B" — an anomaly's change against the value survey-api measured it from;
 *  only "against $1.2 B" where the share cannot be said (a zero baseline, a value not a number). */
function againstBaseline(change: IListImportChange, fieldOf: FieldOf): string {
  const baseline = change.baseline;
  if (baseline === null) return '';
  const after = numberOf(change.after);
  const against = `against ${
    change.field.startsWith('fig.')
      ? formatMoney(baseline)
      : (fieldOf(change.field) ? formatListValue(fieldOf(change.field)!, baseline) : String(baseline))
  }`;
  if (after === null || baseline === 0) return against;
  return `${changeText((after - baseline) / baseline)} ${against}`;
}

function numberOf(value: unknown): Nullable<number> {
  if (typeof value === 'number') return value;
  if (isRecord(value) && typeof value.v === 'number') return value.v;
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function written(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

const LONG_TEXT_BOX = 'block max-h-40 overflow-y-auto rounded-sm border border-border px-2 py-1';

/** One change of a row, in words, with what became of it. */
function ChangeLine({ change, fieldOf }: { change: IListImportChange; fieldOf: FieldOf }) {
  const notes: string[] = [];
  if (change.held !== null) {
    notes.push(
      change.held === 'anomaly'
        ? [holdLabel(change.held), againstBaseline(change, fieldOf)].filter(Boolean).join(', ')
        : holdLabel(change.held)
    );
  }
  if (change.firstValue) notes.push('first value — never held');
  if (change.applied) notes.push('written');
  if (change.reverted) notes.push('put back');
  const before = changeValueText(change.field, change.before, fieldOf);
  const after = changeValueText(change.field, change.after, fieldOf);
  // A long text (SR-408's D6) is drawn on its lines, each side a box that scrolls past 10rem.
  const long = fieldOf(change.field)?.kind === 'long_text';
  return (
    <li data-change={change.field} data-held={change.held ?? undefined}>
      <span className="font-medium">{importFieldLabel(change.field, fieldOf)}:</span>{' '}
      {long ? (
        <span className="mt-1 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2">
          <span data-before="" className={cn(LONG_TEXT_BOX, LONG_TEXT_WHOLE)}>
            {before}
          </span>
          <span aria-hidden>→</span>
          <span className="sr-only">changed to</span>
          <span data-after="" className={cn(LONG_TEXT_BOX, LONG_TEXT_WHOLE)}>
            {after}
          </span>
        </span>
      ) : (
        <>
          {before} → {after}
        </>
      )}
      {notes.length > 0 && <span className="text-muted-foreground"> — {notes.join('; ')}</span>}
    </li>
  );
}

/** Whether a row holds a change the apply writes only when the row is included. */
export function holdsAnomaly(row: IListImportRow): boolean {
  return row.changes.some((change) => change.held === 'anomaly');
}

export function ImportRows({
  entry,
  fieldOf,
  tab,
  onTab,
  page,
  onPage,
  included,
  onInclude,
  rereads,
  restricted
}: {
  entry: IListImport;
  fieldOf: FieldOf;
  tab: Nullable<ListImportRowFilter>;
  onTab: (tab: Nullable<ListImportRowFilter>) => void;
  page: number;
  onPage: (page: number) => void;
  /** The rows whose held changes the apply writes too — only while the import is `ready`. */
  included: ReadonlySet<number>;
  onInclude: (rowNos: number[], include: boolean) => void;
  /** Asks the rows again — the import's state moved. */
  rereads: string;
  /** The rows are restricted and this reader may not read them: no read is made. */
  restricted: boolean;
}) {
  const asked = `${entry.id}\u0001${tab ?? ''}\u0001${page}`;
  const { data, error } = useAdminData<{ asked: string; rows: Nullable<IListImportRowList> | 'forbidden' }>(
    async (signal) => {
      if (restricted) return { asked, rows: 'forbidden' };
      try {
        return {
          asked,
          rows: await listsApi.importRows(entry.id, { outcome: tab ?? undefined, page }, signal)
        };
      } catch (rowsError) {
        if (rowsError instanceof ApiError && rowsError.status === 403) return { asked, rows: 'forbidden' };
        throw new Error(refusalMessage(rowsError, 'The rows could not be read'));
      }
    },
    [asked, rereads, restricted]
  );
  const shown = data?.asked === asked ? data.rows : undefined;
  const choosing = entry.state === 'ready';
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  React.useEffect(() => setSaid(null), [asked]);

  if (shown === 'forbidden') {
    return (
      <p role="status" className="text-sm text-muted-foreground" data-import-restricted>
        This file writes a restricted staff field; its rows, result and apply are for holders of
        restricted access.
      </p>
    );
  }

  const rows = shown?.rows ?? [];
  const heldOnPage = rows.filter(holdsAnomaly).map((row) => row.rowNo);

  let tableState: DataTableState;
  if (shown === undefined) {
    tableState = error
      ? { kind: 'error', content: <LoadError message={error} /> }
      : { kind: 'loading' };
  } else if (shown === null) {
    tableState = { kind: 'empty', content: 'This import is no longer there.' };
  } else {
    tableState = {
      kind: 'empty',
      content:
        shown.total > 0 ? (
          <PastTheEnd
            page={page}
            pageSize={IMPORT_ROW_PAGE_SIZE}
            total={shown.total}
            counted={['row', 'rows']}
            onPage={onPage}
          />
        ) : (
          EMPTY_TAB[tab ?? ''] ?? 'No row is in this tab.'
        )
    };
  }

  const columns: IDataColumn<IListImportRow>[] = [
    { id: 'row', header: '#', numeric: true, headClassName: 'w-14', cell: (row) => row.rowNo },
    {
      id: 'who',
      header: 'Who',
      cell: (row) =>
        row.participantId ? (
          <span className="grid">
            <CardLink to={personHubPath(row.participantId)}>
              {row.participantName ?? 'A participant with no name'}
            </CardLink>
            {row.key && <span className="text-xs break-all text-muted-foreground">{row.key}</span>}
          </span>
        ) : (
          <span className="grid">
            <span>Nobody matches</span>
            <span className="text-xs break-all text-muted-foreground">{row.key ?? 'No key in this row'}</span>
          </span>
        )
    },
    {
      id: 'outcome',
      header: 'Outcome',
      cell: (row) => {
        const chip = importOutcomeChip(row.outcome);
        return <StatusChip tone={chip.tone}>{chip.label}</StatusChip>;
      }
    },
    {
      id: 'changes',
      header: 'Changes',
      cell: (row) => (
        <div className="grid gap-1">
          {row.errors.length > 0 && (
            <ul className="grid gap-0.5 text-destructive">
              {row.errors.map((rowError, index) => (
                <li key={index} data-row-error={rowError.heading}>
                  <span className="font-medium">{rowError.heading}:</span> {rowError.message}
                </li>
              ))}
            </ul>
          )}
          {row.changes.length > 0 && (
            <ul className="grid gap-0.5">
              {row.changes.map((change) => (
                <ChangeLine key={change.field} change={change} fieldOf={fieldOf} />
              ))}
            </ul>
          )}
          {row.outcome === 'matched' && row.changes.length === 0 && row.errors.length === 0 && (
            <span className="text-muted-foreground">Nothing would change.</span>
          )}
          {choosing && holdsAnomaly(row) && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={included.has(row.rowNo)}
                onCheckedChange={(checked) => onInclude([row.rowNo], checked === true)}
                aria-label={`Write row ${row.rowNo}'s held change too`}
              />
              Write this one too
            </label>
          )}
        </div>
      )
    }
  ];

  return (
    <section className="grid gap-3" aria-labelledby="import-rows-title" data-import-rows>
      <h2 id="import-rows-title" className="text-base font-semibold">
        Rows
      </h2>
      <nav aria-label="Rows" className="flex flex-wrap gap-1.5">
        {ROW_TABS.map((one) => (
          <Button
            key={one.label}
            type="button"
            size="sm"
            variant={one.filter === tab ? 'default' : 'outline'}
            aria-pressed={one.filter === tab}
            onClick={() => onTab(one.filter)}
          >
            {one.label}
          </Button>
        ))}
      </nav>
      {error && shown !== undefined && (
        <LoadError message={`Could not refresh — showing the rows as last read. ${error}`} />
      )}
      <DataTable
        label="Rows of the file"
        columns={columns}
        rows={shown === undefined || shown === null || rows.length === 0 ? null : rows}
        rowKey={(row) => String(row.rowNo)}
        rowProps={(row) =>
          ({ 'data-import-row': row.rowNo, 'data-outcome': row.outcome }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        state={tableState}
      />
      {shown && shown.total > 0 && (
        <Pager page={page} pageSize={IMPORT_ROW_PAGE_SIZE} total={shown.total} onPage={onPage} />
      )}
      {choosing && heldOnPage.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              onInclude(heldOnPage, true);
              setSaid(
                `Every held change on this page is included — ${heldOnPage.length} ${
                  heldOnPage.length === 1 ? 'row' : 'rows'
                }. Held rows on other pages stay as they are.`
              );
            }}
          >
            Include every held change
          </Button>
          {said && (
            <span role="status" className="text-sm text-muted-foreground">
              {said}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

const EMPTY_TAB: Record<string, string> = {
  '': 'The file holds no data row.',
  changed: 'No row would change anything.',
  held: 'No change is held.',
  error: 'Every row can be written.',
  unmatched: 'Every row matches a record.'
};
